import React from "react";

/**
 * The scoring table on the Finish tab — a fixed set of parameters, each out
 * of a max score, with the score obtained entered by hand (this is not
 * derived from checkpoint answers; it's a separate manual rollup the auditor
 * fills in before submitting).
 *
 * `value` is the parameters array as it will be sent to the server - each
 * row is exactly what `scoring_snapshot.parameters` holds, so there is no
 * translation between what this table shows and what gets saved.
 */
export const DEFAULT_SCORING_PARAMETERS = [
  { parameter: "Design & Installation", max_score: 5 },
  { parameter: "Preventive Maintenance", max_score: 5 },
  { parameter: "Training", max_score: 5 },
  { parameter: "Lock out Tag out (LOTO)", max_score: 5 },
  { parameter: "Drawings (As- built) /Documents", max_score: 5 },
];

/**
 * A fresh, unscored row set — what a draft audit starts from.
 *
 * `score_obtained` starts at "" rather than 0: scoring is mandatory for
 * submit, and a silent default of 0 would already read as "a score", letting
 * an audit through that nobody actually scored.
 */
export function blankScoringRows() {
  return DEFAULT_SCORING_PARAMETERS.map((p) => ({ ...p, score_obtained: "" }));
}

/**
 * The full `scoring_snapshot` JSON sent with submit - the rows themselves
 * plus both totals, so the client's report never has to re-sum them from
 * the parameters array and risk disagreeing with what this table showed.
 */
export function buildScoringSnapshot(rows) {
  const parameters = (rows && rows.length ? rows : blankScoringRows()).map((r) => ({
    parameter: r.parameter,
    max_score: Number(r.max_score) || 0,
    score_obtained: Number(r.score_obtained) || 0,
  }));
  return {
    parameters,
    max_total: parameters.reduce((n, r) => n + r.max_score, 0),
    score_obtained_total: parameters.reduce((n, r) => n + r.score_obtained, 0),
  };
}

export default function ElectricalScoringTable({ rows, onChange, readOnly }) {
  const data = rows && rows.length ? rows : blankScoringRows();
  const maxTotal = data.reduce((n, r) => n + (Number(r.max_score) || 0), 0);
  const obtainedTotal = data.reduce((n, r) => n + (Number(r.score_obtained) || 0), 0);

  const setScore = (i, raw) => {
    if (!onChange) return;
    // Allow a momentarily empty box while typing rather than snapping to 0,
    // but never let the stored value exceed that row's own max.
    const max = Number(data[i].max_score) || 0;
    const num = raw === "" ? "" : Math.min(max, Math.max(0, Number(raw)));
    onChange(data.map((r, idx) => (idx === i ? { ...r, score_obtained: num } : r)));
  };

  return (
    <div className="scoring-table">
      <div className="scoring-table-head">SCORING TABLE</div>
      <table>
        <thead>
          <tr>
            <th>Parameter</th>
            <th>Max Score</th>
            <th>Score Obtained</th>
          </tr>
        </thead>
        <tbody>
          {data.map((r, i) => (
            <tr key={r.parameter}>
              <td>{r.parameter}</td>
              <td>{r.max_score}</td>
              <td className="scoring-obtained">
                {readOnly ? (
                  Number(r.score_obtained) || 0
                ) : (
                  <input
                    type="number"
                    min={0}
                    max={r.max_score}
                    step="0.01"
                    value={r.score_obtained}
                    onChange={(e) => setScore(i, e.target.value)}
                  />
                )}
              </td>
            </tr>
          ))}
          <tr className="scoring-cumulative">
            <td>Cumulative</td>
            <td>{maxTotal}</td>
            <td>{obtainedTotal.toFixed(2)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
